<template>
  <!--
    Publish modal — the FORWARD action: review what changed (draft vs live),
    preview the reading as the seeker will get it, then publish. Rollback to a
    past version lives separately (Game History), so "publish" never means
    "roll back".
  -->
  <div class="pm-overlay" @click.self="$emit('close')">
    <div class="pm-shell">
      <header class="pm-head">
        <span class="pm-title">Publish Live</span>
        <button type="button" class="pm-close" aria-label="Close" @click="$emit('close')">✕</button>
      </header>

      <div class="pm-body">
        <p class="pm-lede">Here's what seekers will start getting once you publish.</p>

        <!-- What changed -->
        <div v-if="loading" class="pm-loading">Checking what changed…</div>
        <ul v-else-if="changes.length" class="pm-changes">
          <li v-for="(c, i) in changes" :key="i" class="pm-change">
            <span class="pm-change-label">{{ c.label }}</span>
            <div class="pm-change-delta">
              <span class="pm-before">{{ c.before || ' - ' }}</span>
              <span class="pm-arrow" aria-hidden="true">→</span>
              <span class="pm-after">{{ c.after || ' - ' }}</span>
            </div>
          </li>
        </ul>
        <p v-else class="pm-nochange">Nothing has changed since your last publish.</p>
      </div>

      <footer class="pm-foot">
        <button type="button" class="pm-preview" @click="$emit('preview')">▶ Preview the reading</button>
        <div class="pm-foot-right">
          <button type="button" class="pm-cancel" @click="$emit('close')">Cancel</button>
          <button
            type="button" class="pm-publish"
            :disabled="publishing || !changes.length" @click="doPublish"
          >{{ publishing ? 'Publishing…' : 'Publish Live' }}</button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { getPublishDiff, publishReadingGame } from '@/api-service'
import { parseAxiosError } from '@/utils/general-utils'

const props = defineProps({
  skillKey: { type: String, required: true },
  runNonce: { type: Number, default: 0 },
})
const emit = defineEmits(['close', 'preview', 'published'])

const changes = ref([])
const loading = ref(false)
const publishing = ref(false)
const error = ref('')

async function load() {
  loading.value = true
  error.value = ''
  try {
    changes.value = await getPublishDiff(props.skillKey)
  } catch (e) {
    changes.value = []
    error.value = parseAxiosError(e)?.message || ''
  } finally {
    loading.value = false
  }
}

async function doPublish() {
  if (publishing.value) return
  publishing.value = true
  try {
    await publishReadingGame(props.skillKey)
    emit('published')
    emit('close')
  } catch (e) {
    error.value = parseAxiosError(e)?.message || 'Publish failed. Try again.'
  } finally {
    publishing.value = false
  }
}

watch(() => props.runNonce, load, { immediate: true })
</script>

<style scoped>
.pm-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(8, 4, 20, 0.7); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center; padding: 24px;
}
.pm-shell {
  width: 100%; max-width: 560px; max-height: 86vh;
  display: flex; flex-direction: column;
  background: #160c2b; border: 1px solid rgba(195, 169, 255, 0.22);
  border-radius: 14px; box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5); overflow: hidden;
}
.pm-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid rgba(195, 169, 255, 0.14);
}
.pm-title { font-family: 'Cinzel', serif; font-size: 15px; letter-spacing: 0.04em; color: #C9A94E; }
.pm-close { background: transparent; border: none; color: rgba(212, 196, 232, 0.7); font-size: 16px; cursor: pointer; }
.pm-body { flex: 1; min-height: 0; overflow-y: auto; padding: 16px 18px; }
.pm-lede { margin: 0 0 14px; font-size: 13px; color: rgba(212, 196, 232, 0.7); }
.pm-loading, .pm-nochange { font-size: 13px; font-style: italic; color: rgba(195, 169, 255, 0.6); }
.pm-changes { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }
.pm-change {
  padding: 10px 12px; border-radius: 10px;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(195, 169, 255, 0.14);
}
.pm-change-label {
  display: block; font-family: 'Cinzel', serif; font-size: 11px;
  letter-spacing: 0.05em; text-transform: uppercase; color: #C9A94E; margin-bottom: 6px;
}
.pm-change-delta { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; line-height: 1.45; }
.pm-before { flex: 1; color: rgba(212, 196, 232, 0.5); text-decoration: line-through; }
.pm-arrow { color: rgba(201, 169, 78, 0.8); flex-shrink: 0; }
.pm-after { flex: 1; color: #E8D8FF; }
.pm-foot {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 18px; border-top: 1px solid rgba(195, 169, 255, 0.14); gap: 12px;
}
.pm-preview {
  background: transparent; border: 1px solid rgba(195, 169, 255, 0.4);
  color: #E8D8FF; border-radius: 999px; padding: 7px 14px; font-size: 13px; cursor: pointer;
}
.pm-preview:hover { border-color: #C9A94E; }
.pm-foot-right { display: inline-flex; align-items: center; gap: 10px; }
.pm-cancel { background: transparent; border: none; color: rgba(212, 196, 232, 0.6); font-size: 13px; cursor: pointer; }
.pm-publish {
  background: #C9A94E; border: none; color: #160c2b; font-weight: 700;
  border-radius: 999px; padding: 8px 18px; font-size: 13px; cursor: pointer;
}
.pm-publish:disabled { opacity: 0.4; cursor: default; }
</style>
